<template>
  <WidgetBaseComponent ref="widgetRef" class="actions-resume-container" :loadedData="allDataLoaded && !showSpinner">
    <!-- HEADER -->
    <template #header>
      <div class="header-bar">
        <h2>{{ widgetData.title }}</h2>
        <button :class="[icons.openWindow, 'open-window-button', 'lf-icon__md']" @click="goToActionList"></button>
      </div>
    </template>

    <!-- BODY -->
    <template #body>
      <div class="resumes-container">
        <LfWhiteCounterComponent
          v-for="(counter, index) in widgetData.actions"
          :key="`counter-${index}`"
          :info="counter"
          class="lf-counter"
        />
      </div>
    </template>
  </WidgetBaseComponent>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import useRouter from '@/composables/useRouter'
import WidgetBaseComponent from '@/components/widgets/WidgetBaseComponent.vue'
import LfWhiteCounterComponent from '@/components/counters/LfWhiteCounterComponent.vue'
import { Icons } from '@/icons/icons'
import { URLS } from '@/router/routes/urlRoutes'
import { ContextName } from '@/store/types/storeGlobalTypes'
import useSelectedRegister from '@/composables/useSelectedRegister'
import useStage from '@/composables/useStage'
import useGlobalVisionWidgets from '@/components/expedients/ExpedientCourts/composables/useGlobalVisionWidgets'

// INTERFACES
interface Resume {
  qty: number
  title: string
}

interface WidgetData {
  title: string
  actions: Resume[]
}

// CONSTS
const icons = {
  openWindow: Icons.OPEN_WINDOW
}

const contexts = {
  actionCounters: ContextName.EXPEDIENT_ACTIONS_RESUME_WIDGET,
  expedientContext: ContextName.EXPEDIENTS
}

// COMPOSABLES
const expedientRegister = useSelectedRegister({ entityName: contexts.expedientContext })
const { selectedRegisterId: selectedExpedientId } = expedientRegister

const { expedientStage } = useStage()
const { fetchExpedientLastActionsWidgetData } = useGlobalVisionWidgets()

// ROUTER
const { goToRoute } = useRouter()

// DATA
const widgetRef = ref(null)

const widgetData = ref<WidgetData>({
  title: '',
  actions: []
})

const showSpinner = ref(true)

// COMPUTED
const expedientStageState = computed(() => expedientStage.value)

const allDataLoaded = computed(() => {
  return !!widgetData.value.title
})

// METHODS
const fetchActionCounters = async () => {
  showSpinner.value = true

  widgetData.value = await fetchExpedientLastActionsWidgetData({
    selectedExpedientId: selectedExpedientId.value,
    filter: {
      source: contexts.actionCounters,
      idStage: expedientStageState.value
    }
  })

  showSpinner.value = false
}

const goToActionList = () => {
  goToRoute({
    name: URLS.ACTION_ALL
  })
}

// WATCHERS
watch(expedientStageState, () => {
  fetchActionCounters()
})

// INIT
fetchActionCounters()
</script>

<style lang="scss" scoped>
.actions-resume-container {
  --lf-counter-height: auto;
  --lf-counter-padding-top: $spacing-xs;
  min-height: 150px;
  border-radius: $cornerRadius-md;
  border: 1px solid $main-300;

  .header-bar {
    @include flex($justify-content: space-between);
    width: 100%;

    .open-window-button {
      color: $main-1000;
    }
  }

  .resumes-container {
    display: grid;
    grid-template-columns: 1fr 1fr;
    width: 100%;
    gap: 8px;

    .lf-counter {
      width: 100%;
    }
  }
}

@media (min-width: 1536px) {
  .actions-resume-container {
    .resumes-container {
      grid-template-columns: 1fr 1fr 1fr 1fr;
    }
  }
}

@media (min-width: 1920px) {
  .actions-resume-container {
    .resumes-container {
      grid-template-columns: 1fr 1fr 1fr 1fr;
      display: flex;
    }
  }
}
</style>
